import "./Cursor.scss";

import React, { FC, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import NavContext from '../Layout/Nav/NavProvider';

const Cursor: FC = () => {
  const defaultText = 'Click';
  const [isActive, setIsActive] = useState(false);  
  const [isDisabled, setIsDisabled] = useState(false); 
  const [isSmall, setIsSmall] = useState(false);
  const [cursorTextColor, setCursorTextColor] = useState<string>();
  const [cursorText, setCursorText] = useState(defaultText);
  const [cursorRotateText, setCursorRotateText] = useState<string[]>([]);  
  const [cursorPosition, setCursorPosition] = useState<{ top: string, left: string }>({ top: '-100vw', left: '-100vw' });
  const cursor = useRef<HTMLDivElement>();
  let clickableElements;
  const linkableElements = ['button', 'a', '.cursor-click'];
  const { isMobile, setUserInteracted, userInteracted } = useContext(NavContext);

  useEffect(() => {
    if (isMobile()) {
      return;
    }
    
    document.addEventListener("click", () => {
      if(!userInteracted) {
        setUserInteracted(true);
      }
    });
  }, [])

  useLayoutEffect(() => {
    if (isMobile() || !cursor?.current) {
      return;
    }

    document.body.appendChild(cursor.current);
    
    const mouseMoveHandler = (e) => {
      if (isDisabled) {
        return;
      }
      if (e.target.dataset?.cursorText && e.target.dataset.cursorText !== cursorText) {
        setCursorText(e.target.dataset.cursorText || defaultText);
      }
      setCursorPosition({ top: `${e.y}px`, left: `${e.x}px` });
    };

    window.addEventListener('mousemove', mouseMoveHandler);

    const mouseenterHandler = (e) => {
      const el: HTMLElement = e.target;    
      if (el.dataset?.cursorDisabled) {
        setIsDisabled(true);
      }  
      setIsActive(true);
      if (el.dataset?.cursorTextSmall) {
        setIsSmall(true);
      }
      if (el.dataset?.cursorTextColor) {
        setCursorTextColor(el.dataset.cursorTextColor);
      }
      setCursorText(el.dataset.cursorText || defaultText);

      if (el.dataset.cursorRotateText) {
        const cursorRotateText = el.dataset?.cursorRotateText.trim();
        const rotateText: string[] = cursorRotateText ? Array.from(cursorRotateText) : [];
        const cursorRotateTextTotalWords = cursorRotateText.split(' ').length;
        if (cursorRotateTextTotalWords > 1) {
          rotateText.push(' ');
        }
        setCursorRotateText(rotateText);
      }
    };

    const mouseoutHandler = () => {
      setIsDisabled(false);
      setIsActive(false);
      setIsSmall(false);
      setCursorText(defaultText);
      setCursorRotateText([]);
      setCursorTextColor(undefined)
    };

    const mouseInteractionHandler = () => {
      clickableElements = document.querySelectorAll<HTMLElement>(linkableElements.join(','));
      
      clickableElements.forEach(link => {
        link.addEventListener('mouseenter', mouseenterHandler);
        link.addEventListener('mouseleave', mouseoutHandler);
      });
    };

    mouseInteractionHandler();

    return () => {
      document.body.removeChild(cursor.current);
      window.removeEventListener('mousemove', mouseMoveHandler);
      if (clickableElements) {
        clickableElements.forEach(link => {
          link.removeEventListener('mouseenter', mouseenterHandler);
          link.removeEventListener('mouseout', mouseoutHandler);
        });
      }
    };
  }, [cursor.current]);

  return (
    <div ref={cursor} className={`cursor-wrapper ${isDisabled ? 'cursor-wrapper--disabled' : ''}`} style={cursorPosition}>
      <div className={`cursor ${isActive ? 'cursor--active' : ''}`}></div>
      <div
        className={`cursor__text ${isActive ? 'cursor__text--active' : ''} ${isSmall ? 'cursor__text--small' : ''}`}
        style={{ color: cursorTextColor }}
      >{cursorText}{isActive}</div>
      <div className="cursor__rotate-text">
      {
        cursorRotateText.map((char, index, array) => {
          return (
            <span
              key={`cursor-char-${char}-${index}`}
              className="cursor__char"
              style={{ transform: `rotate(${(360 / array.length) * index}deg)` }}
            >
              {char}
            </span>
          )
        })
      }
      </div>
    </div>
  );
};

export default Cursor;
